import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI, candidatesAPI } from '../../utils/api';
import Button from '../shared/Button';
import './RegisterDashboard.css';

const RegisterDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inviteData, setInviteData] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    // Basic Info
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',

    // Professional Info
    linkedIn: '',
    portfolio: '',
    github: '',
    yearsOfExperience: '',
    currentRole: '',

    // Availability
    availableFrom: '',
    preferredInterviewTime: 'morning', // morning, afternoon, evening
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

    // Terms
    acceptTerms: false,
    acceptPrivacy: false
  });

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link. Please contact your recruiter.');
      setLoading(false);
      return;
    }

    verifyInvite();
  }, [token]);

  const verifyInvite = async () => {
    try {
      setLoading(true);
      const response = await authAPI.verifyInvite(token);

      if (response.data.success) {
        setInviteData(response.data.invite);
        setFormData(prev => ({
          ...prev,
          email: response.data.invite.email,
          firstName: response.data.invite.firstName || '',
          lastName: response.data.invite.lastName || ''
        }));
      } else {
        setError(response.data.message || 'Invalid or expired invitation');
      }
    } catch (err) {
      console.error('Invite verification failed:', err);
      setError(err.response?.data?.error || 'Failed to verify invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateStep = (step) => {
    setError(null);

    switch (step) {
      case 1:
        if (!formData.firstName.trim()) {
          setError('First name is required');
          return false;
        }
        if (!formData.lastName.trim()) {
          setError('Last name is required');
          return false;
        }
        if (!formData.email.trim()) {
          setError('Email is required');
          return false;
        }
        if (!formData.password || formData.password.length < 8) {
          setError('Password must be at least 8 characters');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          return false;
        }
        return true;

      case 2:
        // Professional info is optional but validate formats if provided
        if (formData.linkedIn && !formData.linkedIn.includes('linkedin.com')) {
          setError('Please enter a valid LinkedIn URL');
          return false;
        }
        if (formData.yearsOfExperience && isNaN(formData.yearsOfExperience)) {
          setError('Years of experience must be a number');
          return false;
        }
        return true;

      case 3:
        if (!formData.availableFrom) {
          setError('Please select your available start date');
          return false;
        }
        return true;

      case 4:
        if (!formData.acceptTerms) {
          setError('You must accept the Terms & Conditions');
          return false;
        }
        if (!formData.acceptPrivacy) {
          setError('You must accept the Privacy Policy');
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
    setError(null);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;

    try {
      setLoading(true);
      setError(null);

      // Register user
      const registrationData = {
        token,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        linkedIn: formData.linkedIn,
        portfolio: formData.portfolio,
        github: formData.github,
        yearsOfExperience: parseInt(formData.yearsOfExperience) || 0,
        currentRole: formData.currentRole,
        availableFrom: formData.availableFrom,
        preferredInterviewTime: formData.preferredInterviewTime,
        timezone: formData.timezone
      };

      const response = await authAPI.register(registrationData);

      if (response.data.success) {
        // Navigate to email verification page
        navigate('/verify-email', {
          state: {
            email: formData.email,
            message: 'Registration successful! Please check your email to verify your account.'
          }
        });
      }
    } catch (err) {
      console.error('Registration failed:', err);
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !inviteData) {
    return (
      <div className="register-dashboard">
        <div className="loading-state">
          <div className="cyber-spinner"></div>
          <p>Verifying invitation...</p>
        </div>
      </div>
    );
  }

  if (error && !inviteData) {
    return (
      <div className="register-dashboard">
        <div className="error-state">
          <h2>Invalid Invitation</h2>
          <p>{error}</p>
          <Button variant="outline" onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="register-dashboard">
      <div className="register-container">
        <div className="register-header">
          <h1>Welcome to SecHire</h1>
          <p>Complete your profile to get started</p>
          {inviteData && (
            <div className="invite-info">
              <span className="invite-label">Position:</span>
              <span className="invite-value">{inviteData.position || 'Security Consultant'}</span>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="progress-bar">
          <div className="progress-steps">
            {[1, 2, 3, 4].map((step) => (
              <div
                key={step}
                className={`progress-step ${currentStep >= step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}
              >
                <div className="step-circle">{step}</div>
                <div className="step-label">
                  {step === 1 && 'Basic Info'}
                  {step === 2 && 'Professional'}
                  {step === 3 && 'Availability'}
                  {step === 4 && 'Review'}
                </div>
              </div>
            ))}
          </div>
          <div className="progress-line">
            <div
              className="progress-fill"
              style={{ width: `${((currentStep - 1) / 3) * 100}%` }}
            ></div>
          </div>
        </div>

        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* Step 1: Basic Information */}
        {currentStep === 1 && (
          <div className="form-step">
            <h2>Basic Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  className="cyber-input"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="John"
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  className="cyber-input"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Doe"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                className="cyber-input"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="john.doe@example.com"
                disabled
                required
              />
              <small className="form-hint">This email was provided by your recruiter</small>
            </div>

            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                name="phone"
                className="cyber-input"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  name="password"
                  className="cyber-input"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Minimum 8 characters"
                  required
                />
                <small className="form-hint">At least 8 characters</small>
              </div>
              <div className="form-group">
                <label>Confirm Password *</label>
                <input
                  type="password"
                  name="confirmPassword"
                  className="cyber-input"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Re-enter password"
                  required
                />
              </div>
            </div>

            <div className="form-actions">
              <Button variant="ghost" onClick={() => navigate('/login')}>
                Already have an account?
              </Button>
              <Button variant="primary" onClick={handleNext}>
                Next: Professional Info →
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Professional Information */}
        {currentStep === 2 && (
          <div className="form-step">
            <h2>Professional Information</h2>
            <p className="step-description">Help us understand your background (optional)</p>

            <div className="form-group">
              <label>LinkedIn Profile</label>
              <input
                type="url"
                name="linkedIn"
                className="cyber-input"
                value={formData.linkedIn}
                onChange={handleInputChange}
                placeholder="https://linkedin.com/in/johndoe"
              />
            </div>

            <div className="form-group">
              <label>Portfolio Website</label>
              <input
                type="url"
                name="portfolio"
                className="cyber-input"
                value={formData.portfolio}
                onChange={handleInputChange}
                placeholder="https://johndoe.com"
              />
            </div>

            <div className="form-group">
              <label>GitHub Profile</label>
              <input
                type="url"
                name="github"
                className="cyber-input"
                value={formData.github}
                onChange={handleInputChange}
                placeholder="https://github.com/johndoe"
              />
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Years of Experience</label>
                <input
                  type="number"
                  name="yearsOfExperience"
                  className="cyber-input"
                  value={formData.yearsOfExperience}
                  onChange={handleInputChange}
                  placeholder="5"
                  min="0"
                  max="50"
                />
              </div>
              <div className="form-group">
                <label>Current Role</label>
                <input
                  type="text"
                  name="currentRole"
                  className="cyber-input"
                  value={formData.currentRole}
                  onChange={handleInputChange}
                  placeholder="Security Engineer"
                />
              </div>
            </div>

            <div className="form-actions">
              <Button variant="outline" onClick={handleBack}>
                ← Back
              </Button>
              <Button variant="primary" onClick={handleNext}>
                Next: Availability →
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Availability */}
        {currentStep === 3 && (
          <div className="form-step">
            <h2>Interview Availability</h2>
            <p className="step-description">When are you available for the assessment?</p>

            <div className="form-group">
              <label>Available From *</label>
              <input
                type="date"
                name="availableFrom"
                className="cyber-input"
                value={formData.availableFrom}
                onChange={handleInputChange}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>

            <div className="form-group">
              <label>Preferred Interview Time *</label>
              <div className="radio-group">
                <label className="radio-option">
                  <input
                    type="radio"
                    name="preferredInterviewTime"
                    value="morning"
                    checked={formData.preferredInterviewTime === 'morning'}
                    onChange={handleInputChange}
                  />
                  <span>Morning (9AM - 12PM)</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="preferredInterviewTime"
                    value="afternoon"
                    checked={formData.preferredInterviewTime === 'afternoon'}
                    onChange={handleInputChange}
                  />
                  <span>Afternoon (12PM - 5PM)</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="preferredInterviewTime"
                    value="evening"
                    checked={formData.preferredInterviewTime === 'evening'}
                    onChange={handleInputChange}
                  />
                  <span>Evening (5PM - 9PM)</span>
                </label>
              </div>
            </div>

            <div className="form-group">
              <label>Timezone</label>
              <input
                type="text"
                name="timezone"
                className="cyber-input"
                value={formData.timezone}
                disabled
              />
              <small className="form-hint">Automatically detected from your browser</small>
            </div>

            <div className="info-box">
              <h4>📅 Next Steps</h4>
              <p>After registration, our recruitment team will schedule a Microsoft Teams meeting with you based on your availability.</p>
            </div>

            <div className="form-actions">
              <Button variant="outline" onClick={handleBack}>
                ← Back
              </Button>
              <Button variant="primary" onClick={handleNext}>
                Next: Review →
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Review & Submit */}
        {currentStep === 4 && (
          <div className="form-step">
            <h2>Review & Submit</h2>
            <p className="step-description">Please review your information before submitting</p>

            <div className="review-section">
              <h3>Personal Information</h3>
              <div className="review-grid">
                <div className="review-item">
                  <span className="review-label">Name:</span>
                  <span className="review-value">{formData.firstName} {formData.lastName}</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Email:</span>
                  <span className="review-value">{formData.email}</span>
                </div>
                {formData.phone && (
                  <div className="review-item">
                    <span className="review-label">Phone:</span>
                    <span className="review-value">{formData.phone}</span>
                  </div>
                )}
              </div>
            </div>

            {(formData.linkedIn || formData.portfolio || formData.github || formData.currentRole) && (
              <div className="review-section">
                <h3>Professional Information</h3>
                <div className="review-grid">
                  {formData.currentRole && (
                    <div className="review-item">
                      <span className="review-label">Current Role:</span>
                      <span className="review-value">{formData.currentRole}</span>
                    </div>
                  )}
                  {formData.yearsOfExperience && (
                    <div className="review-item">
                      <span className="review-label">Experience:</span>
                      <span className="review-value">{formData.yearsOfExperience} years</span>
                    </div>
                  )}
                  {formData.linkedIn && (
                    <div className="review-item">
                      <span className="review-label">LinkedIn:</span>
                      <span className="review-value"><a href={formData.linkedIn} target="_blank" rel="noopener noreferrer">View Profile</a></span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="review-section">
              <h3>Availability</h3>
              <div className="review-grid">
                <div className="review-item">
                  <span className="review-label">Available From:</span>
                  <span className="review-value">{new Date(formData.availableFrom).toLocaleDateString()}</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Preferred Time:</span>
                  <span className="review-value">{formData.preferredInterviewTime}</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Timezone:</span>
                  <span className="review-value">{formData.timezone}</span>
                </div>
              </div>
            </div>

            <div className="terms-section">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleInputChange}
                  required
                />
                <span>I accept the <a href="/terms" target="_blank">Terms & Conditions</a> *</span>
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="acceptPrivacy"
                  checked={formData.acceptPrivacy}
                  onChange={handleInputChange}
                  required
                />
                <span>I accept the <a href="/privacy" target="_blank">Privacy Policy</a> *</span>
              </label>
            </div>

            <div className="form-actions">
              <Button variant="outline" onClick={handleBack}>
                ← Back
              </Button>
              <Button
                variant="success"
                onClick={handleSubmit}
                loading={loading}
                disabled={!formData.acceptTerms || !formData.acceptPrivacy}
              >
                Complete Registration
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegisterDashboard;
